import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { SettingsLink } from '@frontend/settings-routing';
import { SettingsPageLayout, SettingsPage } from '@frontend/settings-ui';
import { AiContainedIcon } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { IndexRow, Text } from '@frontend/design-system';

type PageAccess = {
  aiReceptionist: boolean;
  callIntelTaskSetting: boolean;
};

export const AiSettingsPage = (access: PageAccess) => {
  const { t } = useTranslation('settings');

  return (
    <SettingsPageLayout>
      <SettingsPage
        id='ai-configuration'
        title={t('AI Settings')}
        subtitle={t('Manage your suite of AI-powered tools.')}
      >
        <AISettingsContainer access={access} />
      </SettingsPage>
    </SettingsPageLayout>
  );
};

const AISettingsContainer = ({ access }: { access: PageAccess }) => {
  const { t } = useTranslation('settings', { keyPrefix: 'ai-settings' });

  if (!access.aiReceptionist && !access.callIntelTaskSetting) {
    return (
      <div css={{ height: '100%' }}>
        <Text size='large' color='subdued'>
          {t('You do not have access to these settings. Please contact your administrator for assistance.')}
        </Text>
      </div>
    );
  }

  return (
    <IndexRow>
      {access.aiReceptionist && (
        <IndexRow.Item as={SettingsLink} to='/ai-configuration/ai-receptionist' css={styles.navItem}>
          <IndexRow.Item.Title>
            <div css={styles.titleContainer}>
              <AiContainedIcon size={40} iconName='aiweave-assistant' containerStyles={styles.icon} />
              <div css={styles.titleContent}>
                <Text size='large'>{t('AI Receptionist')}</Text>
                <Text size='medium' color='subdued' css={styles.description}>
                  {t('Configure and train your AI Receptionist to better understand your business and goals.')}
                </Text>
              </div>
            </div>
          </IndexRow.Item.Title>
        </IndexRow.Item>
      )}
      {access.callIntelTaskSetting && (
        <IndexRow.Item
          as={SettingsLink}
          to='/ai-configuration/call-intelligence'
          css={[styles.navItem, { marginTop: theme.spacing(2) }]}
        >
          <IndexRow.Item.Title>
            <div css={styles.titleContainer}>
              <AiContainedIcon size={40} iconName='call-intel-small' containerStyles={styles.icon} />
              <div css={styles.titleContent}>
                <Text size='large'>{t('Call Intelligence')}</Text>
                <Text size='medium' color='subdued' css={styles.description}>
                  {t("Configure how Call Intelligence works to support your team's workflow.")}
                </Text>
              </div>
            </div>
          </IndexRow.Item.Title>
        </IndexRow.Item>
      )}
    </IndexRow>
  );
};

const styles = {
  icon: css`
    padding: ${theme.spacing(0.5)};
    border-radius: ${theme.borderRadius.medium};
    flex-shrink: 0;
  `,
  navItem: css`
    section {
      flex: 1;
      overflow: hidden;
    }
  `,
  titleContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    overflow: hidden;
  `,
  titleContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    flex: 1;
    overflow: hidden;
  `,
  description: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};
